/**
 * A horizontally scrolling track with previous/next arrows.
 *
 * The track is a native scroll container with scroll-snap, so touch, trackpad
 * and keyboard scrolling all work before this script runs. The script only
 * wires the arrows and disables whichever one has nothing left to reveal.
 * Arrows are hidden entirely when everything already fits.
 */
class SnusifyRail extends HTMLElement {
  #track = null;
  #prev = null;
  #next = null;
  /** Recreated on every connect: a controller aborted by a disconnect
      cannot be reused, and listeners bound to it would silently never fire. */
  #abort = null;
  #resize = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    this.#track = this.querySelector('[data-rail-track]');
    this.#prev = this.querySelector('[data-rail-prev]');
    this.#next = this.querySelector('[data-rail-next]');
    if (!this.#track) return;

    const { signal } = this.#abort;
    this.#prev?.addEventListener('click', () => this.#page(-1), { signal });
    this.#next?.addEventListener('click', () => this.#page(1), { signal });
    this.#track.addEventListener('scroll', () => this.#sync(), { passive: true, signal });

    this.#resize = new ResizeObserver(() => this.#sync());
    this.#resize.observe(this.#track);
    this.#sync();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#resize?.disconnect();
  }

  /**
   * Scroll by one visible width, less a card, so the last card of this page
   * becomes the first of the next and the eye keeps its place.
   * @param {number} direction - -1 or 1
   */
  #page(direction) {
    const first = this.#track.firstElementChild;
    const step = first ? first.getBoundingClientRect().width : 0;
    const distance = Math.max(step, this.#track.clientWidth - step);
    // Logical direction: in an RTL document the track scrolls the other way.
    const rtl = getComputedStyle(this.#track).direction === 'rtl' ? -1 : 1;
    this.#track.scrollBy({ left: direction * distance * rtl, behavior: 'smooth' });
  }

  #sync() {
    const { scrollLeft, scrollWidth, clientWidth } = this.#track;
    const overflow = scrollWidth - clientWidth > 1;
    const position = Math.abs(scrollLeft);

    for (const arrow of [this.#prev, this.#next]) {
      if (arrow) arrow.hidden = !overflow;
    }
    if (!overflow) return;

    if (this.#prev) this.#prev.disabled = position <= 1;
    if (this.#next) this.#next.disabled = position >= scrollWidth - clientWidth - 1;
  }
}

if (!customElements.get('snusify-rail')) {
  customElements.define('snusify-rail', SnusifyRail);
}
